import { Injectable, inject } from '@angular/core';
import { CoreActions } from '@next/core-lib';
import { DialogService, DIALOG_RETURN_TYPES } from '@next/core-lib/dialog';
import { LocaleService } from '@next/core-lib/i18n';
import { ofType, Actions, createEffect } from '@ngrx/effects';
import { Store, select } from '@ngrx/store';
import { of, EMPTY } from 'rxjs';
import { switchMap, map, finalize, startWith, withLatestFrom, delay } from 'rxjs/operators';
import { AppState } from 'src/app/shared/models/app.model';

import * as ProprietaryFertilizerActions from './proprietary-fertilizer.actions';
import {
  selectActiveOriginalFertilizers,
  selectOriginalCropAids,
} from './proprietary-fertilizer.selectors';
import { getCropYear } from '../../store/app.selectors';
import { ProprietaryFertilizerDialogComponent } from '../components/proprietary-fertilizer-dialog/proprietary-fertilizer-dialog.component';
import { ProprietaryFertilizerApiService } from '../services/proprietary-fertilizer-api.service';

const DelayAfterUpsertBeforeReload = 3000;

@Injectable()
export class ProprietaryFertilizerEffects {
  protected readonly dialog = inject(DialogService);
  protected readonly locale = inject(LocaleService);
  protected readonly store = inject<Store<AppState>>(Store);
  protected readonly actions$ = inject(Actions);
  protected readonly proprietaryFertilizerApi = inject(ProprietaryFertilizerApiService);

  fetchOriginalCropAids$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProprietaryFertilizerActions.fetchOriginalCropAids),
      switchMap((_) =>
        this.proprietaryFertilizerApi.fetchOriginalCropAids().pipe(
          map((cropAids) => ProprietaryFertilizerActions.setOriginalCropAids({ cropAids })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
          startWith(CoreActions.setLoading({ loading: true })),
        ),
      ),
    ),
  );

  fetchProprietaryFertilizers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProprietaryFertilizerActions.fetchProprietaryFertilizers),
      withLatestFrom(this.store.pipe(select(getCropYear))),
      switchMap(([_, cropYear]) =>
        this.proprietaryFertilizerApi.fetchProprietaryFertilizers(cropYear).pipe(
          map((proprietaryFertilizers) =>
            ProprietaryFertilizerActions.setProprietaryFertilizers({ proprietaryFertilizers }),
          ),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
          startWith(CoreActions.setLoading({ loading: true })),
        ),
      ),
    ),
  );

  upsertProprietaryFertilizer$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProprietaryFertilizerActions.upsertProprietaryFertilizer),
      withLatestFrom(
        this.store.pipe(select(getCropYear)),
        this.store.pipe(select(selectOriginalCropAids)),
      ),
      switchMap(([{ proprietaryFertilizer }, cropYear, originalCropAids]) => {
        const originalCropAid = originalCropAids.find(
          (c) => c.id === proprietaryFertilizer.originalCropAidId,
        );
        return this.proprietaryFertilizerApi
          .upsertProprietaryFertilizer(proprietaryFertilizer, cropYear, originalCropAid)
          .pipe(
            delay(DelayAfterUpsertBeforeReload),
            map(() => ProprietaryFertilizerActions.fetchProprietaryFertilizers()),
            finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
            startWith(CoreActions.setLoading({ loading: true })),
          );
      }),
    ),
  );

  openProprietaryFertilizerDialog$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProprietaryFertilizerActions.openProprietaryFertilizerDialog),
      withLatestFrom(
        this.store.pipe(select(selectActiveOriginalFertilizers)),
        this.store.pipe(select(getCropYear)),
      ),
      switchMap(([{ proprietaryFertilizer }, cropAids, cropYear]) => {
        const isPreviousYearCreate =
          proprietaryFertilizer.id === -1 && cropYear < new Date().getFullYear();

        return isPreviousYearCreate
          ? this.dialog
              .notify({
                title: this.locale.translate('createProprietaryFertilizer'),
                text: this.locale.translate('createFertilizersPreviousYearsWarning'),
              })
              .pipe(switchMap(() => EMPTY))
          : this.dialog
              .form(ProprietaryFertilizerDialogComponent, {
                title: this.locale.translate(
                  proprietaryFertilizer.originalCropAidId > 0
                    ? 'editProprietaryFertilizer'
                    : 'createProprietaryFertilizer',
                ),
                id: proprietaryFertilizer.id,
                originalCropAidId: proprietaryFertilizer.originalCropAidId,
                name: proprietaryFertilizer.name,
                expirationCropYear: proprietaryFertilizer.expirationCropYear,
                nutritionalComposition: proprietaryFertilizer.nutritionalComposition,
                cropAids,
                cropYear,
                confirmButton: true,
              })
              .pipe(
                switchMap(({ result, status }) =>
                  status === DIALOG_RETURN_TYPES.confirm
                    ? of(
                        ProprietaryFertilizerActions.upsertProprietaryFertilizer({
                          proprietaryFertilizer: result,
                        }),
                      )
                    : EMPTY,
                ),
              );
      }),
    ),
  );
}
